import React, { memo, useEffect, useState } from "react";
import { Grid, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@material-ui/core";
import Popup from "app/common/Component/Popup/Popup";
import { Formik, getIn, useFormikContext } from "formik";
import CommonButton from 'app/common/Component/Button/CommonButton';
import SaveIcon from '@material-ui/icons/Save';
import BlockIcon from '@material-ui/icons/Block';
import { useTranslation } from "react-i18next";
import { INTEREST_RATE_TYPE } from "app/common/LocalConst";
import GlobitsSelectInput from "app/common/form/GlobitsSelectInput";
import GlobitsTextField from "app/common/form/GlobitsTextField";
import { formatMoney } from "app/common/LocalFunction";

function SelectInterestRatePopup({
    open,
    handleClose,
    item,
    paymentMethod,
    totalOfMethod,
    onSave
}) {
    const { t } = useTranslation();
    const [localInitialValues, setLocalInitialValues] = useState(null);

    // ================= INIT LOCAL VALUES WHEN OPEN =================
    useEffect(() => {
        if (!open) return;

        const initialType = item?.interestRateType ?? paymentMethod?.interestRateType;
        let initialRate = 0;
        let initialMonths = 0;
        let initialPeriodId = null;

        // Ưu tiên dữ liệu đã lưu
        if (item?.interestRate != null || item?.periodMonths != null) {
            initialPeriodId = item?.interestRatePeriodId || null;
            initialRate = item.interestRate || 0;
            initialMonths = item.periodMonths || 0;
        } else {
            // Fallback theo số tiền
            const periods = paymentMethod?.interestRatePeriods || [];
            const matched = periods.find(p =>
                (p.amountFrom == null || totalOfMethod >= p.amountFrom) &&
                (p.amountTo == null || totalOfMethod <= p.amountTo)
            );

            if (matched) {
                initialPeriodId = matched.id;
                initialRate = matched.interestRate;
                initialMonths = matched.periodMonths;
            }
        }

        setLocalInitialValues({
            interestRateType: initialType,
            interestRate: initialRate,
            periodMonths: initialMonths,
            interestRatePeriodId: initialPeriodId
        });
    }, [open, item, paymentMethod, totalOfMethod]);

    // ================= SELECT / UNSELECT =================
    const handleToggleSelect = (periodId, localValues, setLocalFieldValue) => {
        if (localValues.interestRatePeriodId === periodId) {
            // BỎ CHỌN
            setLocalFieldValue("interestRate", 0);
            setLocalFieldValue("periodMonths", 0);
            setLocalFieldValue("interestRatePeriodId", null);
        } else {
            // CHỌN MỚI
            const period = paymentMethod?.interestRatePeriods
                ?.find(p => p.id === periodId);

            if (!period) return;

            setLocalFieldValue("interestRate", period.interestRate);
            setLocalFieldValue("periodMonths", period.periodMonths);
            setLocalFieldValue("interestRatePeriodId", period.id);
        }
    };

    // ================= SAVE =================
    const handleLocalSubmit = (localValues) => {
        onSave(localValues);
        handleClose();
    };

    if (!open || !localInitialValues) return null;

    return (
        <Popup
            size="md"
            title="Thông tin lãi suất"
            open={open}
            handleClose={handleClose}
        >
            <Formik
                initialValues={localInitialValues}
                enableReinitialize
                onSubmit={handleLocalSubmit}
            >
                {({ values: formikValues, setFieldValue: setLocalFieldValue, handleSubmit }) => (
                    <>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <p><strong>Hình thức thanh toán:</strong> {paymentMethod?.name}</p>
                                <p><strong>Số tiền:</strong> {item?.totalOfMethod} VND</p>
                            </Grid>

                            {/* ===== HÌNH THỨC CHỊU LÃI ===== */}
                            <Grid item xs={4}>
                                <GlobitsSelectInput
                                    name="interestRateType"
                                    label="Hình thức lãi suất"
                                    options={INTEREST_RATE_TYPE}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <GlobitsTextField
                                    name="periodMonths"
                                    label="Kỳ hạn (tháng)"
                                    type="number"
                                    notDelay
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <GlobitsTextField
                                    name="interestRate"
                                    label="Lãi suất (%)"
                                    type="number"
                                    notDelay
                                />
                            </Grid>

                            {/* ===== TABLE ===== */}
                            <Grid item xs={12}>
                                <TableContainer component={Paper} style={{ maxHeight: 300 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width={50} />
                                                <TableCell>Tên kỳ hạn</TableCell>
                                                <TableCell align="right">Từ</TableCell>
                                                <TableCell align="right">Đến</TableCell>
                                                <TableCell align="center">Kỳ hạn</TableCell>
                                                <TableCell align="center">Lãi suất</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {paymentMethod?.interestRatePeriods
                                                ?.filter(p =>
                                                    (p.amountFrom == null || totalOfMethod >= p.amountFrom) &&
                                                    (p.amountTo == null || totalOfMethod <= p.amountTo)
                                                )
                                                ?.map(period => (
                                                    <TableRow
                                                        key={period.id}
                                                        hover
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => handleToggleSelect(period.id, formikValues, setLocalFieldValue)}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Radio
                                                                checked={formikValues.interestRatePeriodId === period.id}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>{period.name}</TableCell>
                                                        <TableCell align="right">
                                                            {period.amountFrom?.toLocaleString()}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {period.amountTo?.toLocaleString()}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {period.periodMonths} th
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {period.interestRate}%
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        </Grid>
                        <div className="flex flex-end mt-2">
                            <CommonButton className="mr-2" onClick={handleSubmit}>
                                <SaveIcon /> {t("Áp dụng")}
                            </CommonButton>
                            <CommonButton className="btn-gray" onClick={handleClose}>
                                <BlockIcon /> {t("cancel")}
                            </CommonButton>
                        </div>
                    </>
                )}
            </Formik>
        </Popup>
    );
}

export default memo(SelectInterestRatePopup);
